import {
  matrizTamanoLabel,
  matrizFlujoTexto,
  matrizRoiTexto
} from "./matrizClasificacionOfficial";

// IP_DEPOT_MATRIZ_INTEGRAL_001R
import { getCommercialStage } from "./commercialStagesOfficial";
import React, { useEffect, useMemo, useState } from "react";
import "./PortfolioDirectionView.css";

const TXT = {
  back: "Volver a Operaci\u00F3n",
  eyebrow: "PORTAFOLIO EJECUTIVO",
  title: "Tablero de proyectos",

  pipelineActive: "Pipeline activo",
  pipelineActiveHint: "Valor total de oportunidades activas",

  pipeline: "Pipeline ponderado",
  pipelineHint: "Monto \u00D7 probabilidad de etapa",

  contracted: "Contratado / En ejecuci\u00F3n",
  contractedHint: "Monto contratado actualmente en ejecuci\u00F3n",

  receivable: "Por cobrar",
  receivableHint: "Saldo pendiente de proyectos ganados",

  active: "Proyectos activos",
  activeHint: "Venta + pausados + ejecuci\u00F3n",

  sales: "VENTA ACTIVA",
  paused: "VENTA DETENIDA TEMPORALMENTE",
  execution: "EN EJECUCI\u00D3N",
  closed: "CERRADOS",
  discarded: "VENTA NO REALIZADA",
  dead: "MUERTO",

  noSales: "Sin proyectos en venta activa",
  noPaused: "Sin proyectos pausados",
  noExecution: "Sin proyectos en ejecuci\u00F3n",
  noClosed: "Sin proyectos cerrados",
  noDiscarded: "Sin ventas no realizadas",
  noDead: "Sin proyectos muertos",
};

// ============================================================
// PCC_DIRECTION_PERSISTED_COMMERCIAL_V2
// Metricas comerciales obtenidas desde datos persistidos.
// ============================================================

function normalize(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function number(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

function clamp(value, min = 0, max = 100) {
  return Math.max(min, Math.min(max, number(value)));
}

function moneyMdp(value) {
  return "$" + (number(value) / 1000000).toFixed(1) + " mdp";
}

function projectAmount(project) {
  const situation = projectSituation(project);

  if (situation === "VENTA" || situation === "PAUSADO") {
    return number(
      project?.montoOportunidad ||
        project?.montoTotal ||
        0
    );
  }

  return contractAmount(project);
}

function contractAmount(project) {
  return number(
    project?.montoContrato ||
      project?.montoContratado ||
      project?.montoTotal ||
      0
  );
}

function collectedAmount(project) {
  return number(
    project?.montoCobrado ||
      project?.montoPagado ||
      project?.cobrado ||
      0
  );
}

function pendingAmount(project) {
  return Math.max(contractAmount(project) - collectedAmount(project), 0);
}

function projectSituation(project) {
  const explicit = normalize(project?.situacionProyecto);
  const closingResult = normalize(project?.resultadoCierre);

  if (explicit === "VENTA_ACTIVA") {
    return "VENTA";
  }

  if (
    explicit === "VENTA_DETENIDA_TEMPORALMENTE" ||
    explicit === "VENTA_DETENIDA" ||
    explicit === "PAUSADO" ||
    explicit === "PAUSADA"
  ) {
    return "PAUSADO";
  }

  if (explicit === "EJECUCION") {
    return "EJECUCION";
  }

  if (
    explicit === "VENTA_NO_REALIZADA" ||
    explicit === "NO_REALIZADA" ||
    explicit === "NO_REALIZADO" ||
    explicit === "PERDIDO"
  ) {
    return "NO_REALIZADA";
  }

  if (
    explicit === "MUERTO" ||
    explicit === "CANCELADO_DEFINITIVAMENTE"
  ) {
    return "MUERTO";
  }

  if (
    explicit === "CERRADO_SATISFACTORIAMENTE" ||
    explicit === "CERRADO"
  ) {
    if (
      closingResult.includes("MUERTO") ||
      closingResult.includes("CANCELADO")
    ) {
      return "MUERTO";
    }

    if (
      closingResult.includes("PERDIDO") ||
      closingResult.includes("NO_REALIZADA") ||
      closingResult.includes("NO REALIZADA")
    ) {
      return "NO_REALIZADA";
    }

    return "CERRADO";
  }

  const commercial = normalize(project?.etapaComercial);

  if (commercial && commercial !== "GANADO") {
    return "VENTA";
  }

  const operational = normalize(
    project?.etapa || project?.fase
  );

  if (operational === "CERRADO") {
    return "CERRADO";
  }

  return "EJECUCION";
}

// IP_DEPOT_COMERCIAL_003D
// Solo transforma la representacion visual de la etapa.
// No modifica el proyecto ni su valor en Google Sheets.
function displayOfficialStage(stage) {
  const projects = Array.isArray(stage?.projects)
    ? stage.projects
    : [];

  const candidates = [
    stage?.key,
    stage?.codigo,
    stage?.code
  ];

  // Si el agrupamiento utiliza nombres de presentacion,
  // recuperar el codigo persistido del proyecto.
  if (projects.length > 0) {
    candidates.push(
      projects[0]?.etapaComercial,
      projects[0]?.etapa_comercial,
      projects[0]?.ETAPA_COMERCIAL
    );
  }

  const official = candidates
    .map(value => getCommercialStage(value))
    .find(Boolean);

  if (official) {
    return official.nombre + " · " +
      official.probabilidad + "%";
  }

  // No inventar un codigo para valores historicos.
  const raw = String(
    stage?.key || stage?.label || ""
  ).trim();

  if (raw.toUpperCase() === "GANADO") {
    return "GANADO · Pendiente de homologación";
  }

  return String(stage?.label || raw) +
    " · Etapa por verificar";
}


// ============================================================
// IP_DEPOT_FIN_FLUJO_001B
// DIMENSION 3 - FLUJO DE COBRANZA
//
// Clasificacion visual usando el porcentaje ya calculado.
// No modifica pagos, contratos, importes ni porcentajes.
// ============================================================

function obtenerEtiquetaFlujoCobranza(porcentaje) {
  if (
    porcentaje === null ||
    porcentaje === undefined ||
    porcentaje === ""
  ) {
    return "Flujo sin determinar";
  }

  const valor = Number(porcentaje);

  if (!Number.isFinite(valor) || valor < 0) {
    return "Flujo sin determinar";
  }

  if (valor > 100) {
    return "Cobranza excedente - revisar";
  }

  if (valor === 0) {
    return "Licitación ganada";
  }

  if (valor < 50) {
    return "Primer pago realizado";
  }

  if (valor < 100) {
    return "50 % ya pagado";
  }

  return "Pagado totalmente";
}

function commercialStage(project) {
  return normalize(
    project?.etapaComercial ||
      project?.reglaComercialAplicada?.codigo ||
      ""
  );
}

function probability(project) {
  return clamp(
    project?.probabilidadComercial ??
      project?.reglaComercialAplicada?.probabilidad ??
      0
  );
}

function roiValue(project) {
  const financialRoi = Number(project?.__financialRoi);

  if (Number.isFinite(financialRoi)) {
    return financialRoi;
  }

  return number(
    project?.roi ||
      project?.ROI ||
      project?.rentabilidad ||
      0
  );
}

function roiBadge(project, financeSummary = {}) {
  const roi = roiValue(project);

  const invested = number(
    financeSummary?.costoNeto ??
      financeSummary?.invertido ??
      financeSummary?.costos ??
      financeSummary?.costoReal ??
      0
  );

  const income = number(
    financeSummary?.ingresos ??
      financeSummary?.ingreso ??
      financeSummary?.cobrado ??
      0
  );

  const explicitRoiValues = [
    project?.__financialRoi,
    project?.roi,
    project?.ROI,
    project?.rentabilidad,
  ];

  const hasExplicitRoi =
    explicitRoiValues.some((value) => {
      if (
        value === null ||
        value === undefined ||
        value === ""
      ) {
        return false;
      }

      return Number.isFinite(Number(value));
    });

  const hasFinancialBase =
    invested > 0 ||
    income > 0 ||
    hasExplicitRoi;

  if (!hasFinancialBase) {
    return {
      text: "ROI pendiente",
      className: "pending",
    };
  }

  if (roi <= 0) {
    return {
      text: "ROI ≤ 0",
      className: "bad",
    };
  }

  if (roi > 18) {
    return {
      text: "ROI >18%",
      className: "great",
    };
  }

  if (roi >= 10) {
    return {
      text: "ROI 10–18%",
      className: "good",
    };
  }

  return {
    text: "ROI <10%",
    className: "warning",
  };
}

function projectSize(project) {
  const explicit = normalize(
    project?.tipoProyecto ||
      project?.projectSize
  );

  if (explicit === "INHOUSE") return "Inhouse";
  if (explicit === "GRANDE") return "Grande";
  if (explicit === "MEDIANO") return "Mediano";
  if (explicit === "CHICO") return "Chico";

  const amount = projectAmount(project);

  if (amount >= 15000000) return "Grande";
  if (amount >= 5000000) return "Mediano";

  return "Chico";
}

function flowPercent(project, financeSummary = {}) {
  // ============================================================
  // PCC_CARD_FINANCE_REAL_V5
  // Prioridad:
  // 1. Resumen financiero real cargado en project
  // 2. financeSummary recibido por Card
  // 3. Calculo historico del proyecto
  // ============================================================

  const summary =
    project?.__financialSummary ||
    financeSummary ||
    {};

  const realAdvance = summary?.avanceCobro;

  if (
    realAdvance !== null &&
    realAdvance !== undefined &&
    realAdvance !== "" &&
    Number.isFinite(Number(realAdvance))
  ) {
    return clamp(Number(realAdvance));
  }

  const contract = contractAmount(project);

  if (contract <= 0) {
    return 0;
  }

  return clamp(
    (collectedAmount(project) / contract) * 100
  );
}

function closingLabel(project) {
  const result = normalize(
    project?.resultadoCierre ||
      project?.situacionProyecto ||
      project?.fase ||
      project?.etapa
  );

  if (result.includes("MUERTO")) return "Muerto";
  if (result.includes("PERDIDO")) return "Perdido";
  if (result.includes("CANCELADO")) return "Cancelado";

  return "Cerrado";
}

function Card({ project, group, financeSummary = {}, onEdit, onFinance }) {
  const roi = roiBadge(project, financeSummary);
  const flow = flowPercent(project);

  const normalizedSituation =
    normalize(project?.situacionProyecto);

  const showRoi =
    normalizedSituation === "EJECUCION" ||
    normalizedSituation === "CERRADO" ||
    normalizedSituation === "CERRADO_SATISFACTORIAMENTE";

  const subtitle =
    (project?.cliente || "Sin cliente") +
    " \u00B7 " +
    moneyMdp(projectAmount(project)) +
    (group === "CIERRE"
      ? " \u00B7 " + closingLabel(project)
      : "");

  return (
    <article className="pdx-card">
      {/* PCC_DIRECTION_SKIN_V2 */}
      <h3
        title={project?.nombre || "Proyecto sin nombre"}
      >
        {project?.nombre || "Proyecto sin nombre"}
      </h3>

      <p>{subtitle}</p>

      <div className="pdx-tags">
        <span className="pdx-size">
          {matrizTamanoLabel(project)}
        </span>

        {showRoi && (
          <span className={"pdx-roi " + roi.className}>
            {roi.text}
          </span>
        )}
      </div>

      <div className="pdx-finance-facts">
        {group === "VENTA" ? (
          <span>
            Valor estimado
            <strong>{moneyMdp(projectAmount(project))}</strong>
          </span>
        ) : (
          <>
            <span>
              Contrato
              <strong>{moneyMdp(contractAmount(project))}</strong>
            </span>

            <span>
              Costo neto
              <strong>{moneyMdp(projectRealCost(
                  project?.__financialSummary ||
                  financeSummary
                ))}</strong>
            </span>
          </>
        )}
      </div>

      {group !== "VENTA" && (
        <>
          <div className="pdx-progress">
            <span style={{ width: flow + "%" }} />
          </div>

          <small>
            {matrizFlujoTexto(project, group, flow)}
          </small>
          {/* IP_DEPOT_MATRIZ_INTEGRAL_002 */}
          {matrizRoiTexto(project) && (
            <small className="pdx-roi-official-label">
              {matrizRoiTexto(project)}
            </small>
          )}
        </>
      )}
          {(typeof onEdit === "function" ||
        typeof onFinance === "function") && (
        <div className="pdx-card-actions">
          {typeof onFinance === "function" && (
            <button
              type="button"
              className="pdx-finance-btn"
              onClick={() => onFinance(project)}
            >
              Finanzas
            </button>
          )}

          {typeof onEdit === "function" && (
            <button
              type="button"
              className="pdx-edit-btn"
              onClick={() => onEdit(project)}
            >
              Editar
            </button>
          )}
        </div>
      )}

      {/* PCC_DIRECTION_FINANCE_BUTTON_V2 */}

    </article>
  );
}


// ============================================================
// PCC_DIRECTION_SIZE_COST_V1
// ============================================================

function projectSizeResolved(project) {
  if (
    normalize(project?.tipoProyecto) === "INHOUSE"
  ) {
    return "Inhouse";
  }

  const size = normalize(
    project?.tamanoProyecto || ""
  );

  if (size === "GRANDE") return "Grande";
  if (size === "MEDIANO") return "Mediano";
  if (size === "CHICO") return "Chico";

  if (
    size === "SIN_MONTO" ||
    number(project?.montoBaseTamano) <= 0
  ) {
    return "Sin monto";
  }

  return "Sin clasificar";
}

function projectRealCost(financeSummary) {
  return number(
    financeSummary?.costoNeto ??
      financeSummary?.invertido ??
      financeSummary?.costos ??
      financeSummary?.costoReal ??
      0
  );
}

// PCC_DIRECTION_SIZE_COST_V1_END
export default function PortfolioDirectionView({
  focusProjectId = null,
  proyectos = [],
  commercialStages = [],
  apiRequest,
  onEditProject,
  onFinanceProject,
  onBack,
  onAnalysis,
  onReview,
}) {

  // FRONT020C_FOCUS_PROJECT
  useEffect(() => {
    if (!focusProjectId) return;

    const project = proyectos.find((p) =>
      String(p?.id ?? p?.proyectoId ?? "") ===
      String(focusProjectId)
    );

    if (!project) return;

    const projectName = String(
      project.nombre ?? project.name ?? project.proyecto ?? ""
    ).trim();

    if (!projectName) return;

    let cancelled = false;
    let frame = null;
    let attempts = 0;
    let cleanupHighlight = null;

    const normalize = (value) =>
      String(value ?? "")
        .normalize("NFD")
        .replace(/[\\u0300-\\u036f]/g, "")
        .replace(/\\s+/g, " ")
        .trim()
        .toLocaleLowerCase("es");

    const expectedName = normalize(projectName);

    const locate = () => {
      if (cancelled) return;

      attempts += 1;

      const candidates = Array.from(
        document.querySelectorAll(
          "h2, h3, h4, h5, strong, [class*='title'], [class*='name']"
        )
      ).filter((element) => {
        const text = normalize(element.textContent);
        return text === expectedName;
      });

      for (const title of candidates) {
        const card = title.closest(
          "[data-project-id], article, [class*='project-card'], " +
          "[class*='portfolio-card'], [class*='projectCard']"
        );

        if (!card) continue;

        const marker = card.getAttribute("data-project-id");

        if (marker && marker !== String(focusProjectId)) {
          continue;
        }

        card.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest"
        });

        const previousOutline = card.style.outline;
        const previousOffset = card.style.outlineOffset;
        const previousTransition = card.style.transition;

        card.style.transition = "outline-color 180ms ease";
        card.style.outline = "3px solid #278270";
        card.style.outlineOffset = "4px";

        const timeout = window.setTimeout(() => {
          card.style.outline = previousOutline;
          card.style.outlineOffset = previousOffset;
          card.style.transition = previousTransition;
        }, 2200);

        cleanupHighlight = () => {
          window.clearTimeout(timeout);
          card.style.outline = previousOutline;
          card.style.outlineOffset = previousOffset;
          card.style.transition = previousTransition;
        };

        return;
      }

      if (attempts < 12) {
        frame = window.requestAnimationFrame(locate);
      }
    };

    frame = window.requestAnimationFrame(locate);

    return () => {
      cancelled = true;
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
      cleanupHighlight?.();
    };
  }, [focusProjectId, proyectos]);


  // ==========================================================
  // PCC_DIRECTION_FINANCE_V1
  // Resumen financiero real desde backend.
  // ==========================================================
  const [financialSummaries, setFinancialSummaries] = useState({});

  // FRONT021B_GENERAL_KPI_V1
  const [generalFinance, setGeneralFinance] = useState({
    loading: true,
    data: null,
    error: null
  });
  

  // PCC_DIRECTION_REFRESH_V4_5F
  const [financeRefreshVersion, setFinanceRefreshVersion] = useState(0);
  const [financeRefreshing, setFinanceRefreshing] = useState(true);

  // PCC_PIPELINE_COMPACT_FINAL_V1_STATE
  // Seleccion visual local. No modifica datos del proyecto.
  const [pipelineStageKey, setPipelineStageKey] = useState("");

  // ==========================================================

  // FRONT021B_GENERAL_KPI_LOADER
  useEffect(() => {
    let alive = true;

    async function loadGeneralFinance() {
      setGeneralFinance({
        loading: true,
        data: null,
        error: null
      });

      const startedAt = performance.now();

      try {
        if (typeof apiRequest !== "function") {
          throw new Error("API_REQUEST_UNAVAILABLE");
        }

        const response = await apiRequest(
          "/proyectos/finanzas/resumen-general"
        );

        if (!alive) return;

        const r = response?.resumen;

        const numericFields = [
          "costoIndirecto",
          "costoBruto",
          "porcentajeCostoIndirecto",
          "proyectosElegibles",
          "proyectosConResumen",
          "proyectosSinResumen"
        ];

        const validNumbers = numericFields.every(
          (field) =>
            typeof r?.[field] === "number" &&
            Number.isFinite(r[field])
        );

        if (
          response?.ok !== true ||
          !r ||
          !validNumbers ||
          r.completo !== true ||
          r.proyectosSinResumen !== 0 ||
          r.proyectosElegibles < 1 ||
          r.proyectosConResumen !== r.proyectosElegibles ||
          r.costoIndirecto < 0 ||
          r.costoBruto < 0 ||
          r.costoIndirecto > r.costoBruto ||
          r.porcentajeCostoIndirecto < 0 ||
          r.porcentajeCostoIndirecto > 100
        ) {
          throw new Error("GENERAL_FINANCE_INCOMPLETE");
        }

        const calculatedPercent =
          r.costoBruto > 0
            ? (r.costoIndirecto / r.costoBruto) * 100
            : 0;

        if (
          Math.abs(
            calculatedPercent - r.porcentajeCostoIndirecto
          ) > 0.02
        ) {
          throw new Error("GENERAL_FINANCE_PERCENT_MISMATCH");
        }

        setGeneralFinance({
          loading: false,
          data: r,
          error: null
        });

        console.info("IP_DEPOT_FIN_GENERAL_TIMING", {
          elapsedMs: Math.round(performance.now() - startedAt),
          eligible: r.proyectosElegibles,
          complete: r.completo
        });
      } catch (error) {
        if (!alive) return;

        console.warn(
          "IP_DEPOT_FIN_GENERAL_UNAVAILABLE",
          error?.message || "UNKNOWN"
        );

        setGeneralFinance({
          loading: false,
          data: null,
          error: "No se pudo validar el consolidado financiero."
        });
      }
    }

    loadGeneralFinance();

    return () => {
      alive = false;
    };
  }, [apiRequest, financeRefreshVersion]);

  // PCC_DIRECTION_FINANCE_CONCURRENCY_V1
  // Carga financiera con concurrencia limitada y cache local.
  // ==========================================================
  useEffect(() => {
    let alive = true;

    // PCC_DIRECTION_REFRESH_V4_5F
    setFinanceRefreshing(true);

    async function cargarResumenesFinancieros() {
      if (typeof apiRequest !== "function") {
        if (alive) {
          setFinanceRefreshing(false);
        }
        return;
      }

      // PCC_CLOSED_FINANCE_LOADER_FIX_V3
      const activeProjects = (Array.isArray(proyectos) ? proyectos : [])
        
        .filter((project) => project?.id || project?.proyectoId);

      const uniqueProjects = new Map();

      activeProjects.forEach((project) => {
        const id = String(project?.id || project?.proyectoId || "");

        if (!id) {
          return;
        }

        if (!uniqueProjects.has(id)) {
          uniqueProjects.set(id, project);
        }
      });

      const queue = Array.from(uniqueProjects.entries()).map(
        ([id, project]) => ({
          id,
          project,
        })
      );

      const next = {};

      // PCC_DIRECTION_FINANCE_LOAD_V4_4
      // Dos solicitudes maximas en paralelo.
      const concurrency = 2;
      const maxAttempts = 2;
      const retryDelayMs = 500;

      let cursor = 0;

      function wait(ms) {
        return new Promise((resolve) => {
          window.setTimeout(resolve, ms);
        });
      }

      async function loadOne(item) {
        let lastError = null;

        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
          if (!alive) {
            return null;
          }

          try {
            const response = await apiRequest(
              "/proyectos/" +
                encodeURIComponent(item.id) +
                "/finanzas/resumen"
            );

            if (!alive) {
              return null;
            }

            if (response?.resumen) {
              return response.resumen;
            }

            throw new Error("Respuesta financiera sin resumen.");
          } catch (error) {
            lastError = error;

            if (attempt < maxAttempts) {
              console.warn(
                "Resumen financiero temporalmente no disponible; reintentando",
                item.id,
                "intento",
                attempt,
                error?.message || error
              );

              await wait(retryDelayMs);
            }
          }
        }

        console.warn(
          "Resumen financiero no disponible despues de retry",
          item.id,
          lastError?.message || lastError
        );

        return null;
      }

      async function worker() {
        while (alive) {
          const index = cursor;
          cursor += 1;

          if (index >= queue.length) {
            return;
          }

          const item = queue[index];

          const summary = await loadOne(item);

          if (!alive) {
            return;
          }

          if (summary) {
            next[item.id] = summary;
          }
        }
      }

      const workers = Array.from(
        {
          length: Math.min(
            concurrency,
            queue.length
          ),
        },
        () => worker()
      );

      await Promise.all(workers);

      if (!alive) return;

      setFinancialSummaries((current) => ({
        ...current,
        ...next,
      }));
    }

    cargarResumenesFinancieros()
      .catch((error) => {
        console.warn(
          "No se pudieron cargar resumenes financieros para Direccion",
          error
        );
      })
      .finally(() => {
        if (alive) {
          setFinanceRefreshing(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [proyectos, apiRequest, financeRefreshVersion]);
  // ==========================================================
  // PCC_DIRECTION_FINANCE_LOAD_V4_4
  // Segundo loader eliminado.
  // Existe una sola cola financiera controlada.
  // ==========================================================

  const model = useMemo(() => {
    const sales = [];
    const paused = [];
    const execution = [];
    const closed = [];
    const notRealized = [];
    const dead = [];

    proyectos.forEach((rawProject) => {
      const projectId = String(
        rawProject?.id || rawProject?.proyectoId || ""
      );

      const financialSummary =
        financialSummaries[projectId] || null;

      // PCC_FIN_COSTO_NETO_ROI_V1_3
      const invested = number(
        financialSummary?.costoNeto ??
          financialSummary?.invertido ??
          0
      );

      const profit = number(
        financialSummary?.utilidad || 0
      );

      const backendRoi = Number(
        financialSummary?.roi
      );

      const financialRoi =
        Number.isFinite(backendRoi) &&
        financialSummary?.roi !== null &&
        financialSummary?.roi !== undefined
          ? backendRoi
          : invested > 0
            ? (profit / invested) * 100
            : null;

      const project = {
        ...rawProject,
        __financialSummary: financialSummary,
        __financialRoi: financialRoi,
      };

      const situation = projectSituation(project);

      if (situation === "VENTA") {
        sales.push(project);
      } else if (situation === "PAUSADO") {
        paused.push(project);
      } else if (situation === "CERRADO") {
        closed.push(project);
      } else if (situation === "NO_REALIZADA") {
        notRealized.push(project);
      } else if (situation === "MUERTO") {
        dead.push(project);
      } else {
        execution.push(project);
      }
    });

    // ========================================================
    // PIPELINE COMERCIAL
    // Incluye todas las etapas del catalogo, aun cuando esten en 0.
    // ========================================================

    const stages = new Map();

    (commercialStages || []).forEach((rule) => {
      const key = normalize(rule?.codigo);

      if (!key) return;

      stages.set(key, {
        key,
        order: Number(rule?.orden || 999),
        probability: number(rule?.probabilidad),
        label: rule?.nombre || rule?.codigo || key,
        projects: [],
      });
    });

    sales.forEach((project) => {
      const stageKey = commercialStage(project);

      const catalogRule = (commercialStages || []).find(
        (item) => normalize(item?.codigo) === stageKey
      );

      const appliedRule =
        project?.reglaComercialAplicada || {};

      const stageProbability = probability(project);

      if (!stages.has(stageKey)) {
        stages.set(stageKey, {
          key: stageKey || "SIN_ETAPA",
          order: Number(catalogRule?.orden || 999),
          probability: stageProbability,
          label:
            appliedRule?.nombre ||
            catalogRule?.nombre ||
            stageKey ||
            "Sin etapa",
          projects: [],
        });
      }

      stages.get(stageKey).projects.push(project);
    });

    const stageList = Array.from(stages.values())
      .map((stage) => ({
        ...stage,
        total: stage.projects.reduce(
          (sum, project) => sum + projectAmount(project),
          0
        ),
      }))
      .sort((a, b) => a.order - b.order);

    const pipelineActive = sales.reduce(
      (total, project) =>
        total + projectAmount(project),
      0
    );

    const pipeline = sales.reduce(
      (total, project) =>
        total + number(project?.pipelinePonderado ?? 0),
      0
    );

    const contracted = execution.reduce(
      (total, project) =>
        total + contractAmount(project),
      0
    );

    const wonProjects = [
      ...execution,
      ...closed,
    ];

    const receivable = wonProjects.reduce(
      (total, project) => {
        const summary =
          project?.__financialSummary;

        const pending =
          summary &&
          Number.isFinite(Number(summary?.porCobrar))
            ? number(summary.porCobrar)
            : pendingAmount(project);

        return total + pending;
      },
      0
    );

    return {
      sales,
      paused,
      execution,
      closed,
      notRealized,
      dead,
      stageList,

      pipelineActive,
      pipeline,
      contracted,
      receivable,

      activeCount:
        sales.length +
        paused.length +
        execution.length,

      salesTotal: sales.reduce(
        (total, project) =>
          total + projectAmount(project),
        0
      ),

      pausedTotal: paused.reduce(
        (total, project) =>
          total + projectAmount(project),
        0
      ),

      executionTotal: execution.reduce(
        (total, project) =>
          total + contractAmount(project),
        0
      ),

      closedTotal: closed.reduce(
        (total, project) =>
          total + contractAmount(project),
        0
      ),

      discardedTotal: [
        ...notRealized,
        ...dead,
      ].reduce(
        (total, project) =>
          total + projectAmount(project),
        0
      ),
    };
  }, [proyectos, commercialStages, financialSummaries]);
  // ============================================================
      // IP_DEPOT_FIN_017E_RECOVERY
  // Recuperacion temporal mientras se valida FIN-017D.
  // Utiliza resumenes individuales calculados en backend.
  // Incluye todos los proyectos del catalogo sin duplicar IDs.

  const indirectGeneralKpi = useMemo(() => {
    const ids = new Set();

    const totals = {
      indirect: 0,
      gross: 0,
      eligible: 0,
      loaded: 0
    };

    (Array.isArray(proyectos) ? proyectos : []).forEach((project) => {
      const id = String(project?.id || project?.proyectoId || "");

      if (!id || ids.has(id)) {
        return;
      }

      ids.add(id);
      totals.eligible += 1;

      const summary = financialSummaries[id];

      if (
        !summary ||
        summary.costoIndirecto === null ||
        summary.costoIndirecto === undefined ||
        summary.costoIndirecto === "" ||
        summary.costoBruto === null ||
        summary.costoBruto === undefined ||
        summary.costoBruto === ""
      ) {
        return;
      }

      const indirect = Number(summary.costoIndirecto);
      const gross = Number(summary.costoBruto);

      if (
        !Number.isFinite(indirect) ||
        !Number.isFinite(gross) ||
        indirect < 0 ||
        gross < 0 ||
        indirect > gross
      ) {
        return;
      }

      totals.indirect += indirect;
      totals.gross += gross;
      totals.loaded += 1;
    });

    return {
      ...totals,
      missing: totals.eligible - totals.loaded,
      percent:
        totals.gross > 0
          ? (totals.indirect / totals.gross) * 100
          : null
    };
  }, [proyectos, financialSummaries]);

  // IP_DEPOT_FIN_017E_RECOVERY_END

  return (
    <div className="pdx-page">
      <div className="pdx-modebar">
        {/* PCC_DIRECTION_REFRESH_V4_5F */}
        <button
          type="button"
          className="pdx-refresh-button"
          disabled={financeRefreshing}
          aria-busy={financeRefreshing}
          onClick={() => {
            if (financeRefreshing) return;
            setFinanceRefreshing(true);
            setFinanceRefreshVersion((version) => version + 1);
          }}
        >
          {financeRefreshing ? "Actualizando..." : "Actualizar"}
        </button>
        {typeof onAnalysis === "function" && (
          <button
            type="button"
            className="pdx-analysis-button"
            onClick={onAnalysis}
          >
            Vista de análisis
          </button>
        )}

        {typeof onReview === "function" && (
          <button
            type="button"
            className="pdx-review-button"
            onClick={onReview}
          >
            Vista de revisión
          </button>
        )}

        <button type="button" onClick={onBack}>
          {TXT.back}
        </button>
      </div>

      <main className="pdx-shell">
        <div className="pdx-eyebrow">
          {TXT.eyebrow}
        </div>

        <h1>{TXT.title}</h1>

        <section className="pdx-kpis pdx-kpis-v3">
          <article>
            <span>{TXT.pipelineActive}</span>
            <strong>{moneyMdp(model.pipelineActive)}</strong>
            <small>{TXT.pipelineActiveHint}</small>
          </article>

          <article>
            <span>{TXT.pipeline}</span>
            <strong>{moneyMdp(model.pipeline)}</strong>
            <small>{TXT.pipelineHint}</small>
          </article>

          <article>
            <span>{TXT.contracted}</span>
            <strong>{moneyMdp(model.contracted)}</strong>
            <small>{TXT.contractedHint}</small>
          </article>

          <article>
            <span>{TXT.receivable}</span>
            <strong>{moneyMdp(model.receivable)}</strong>
            <small>{TXT.receivableHint}</small>
          </article>

          <article>
            <span>{TXT.active}</span>
            <strong>{model.activeCount}</strong>
            <small>{TXT.activeHint}</small>
          </article>
                  {/* IP_DEPOT_CI_03D_1_CARD */}
          <article>
            <span>Costo indirecto general</span>

            <strong>
              {generalFinance.loading
                ? "Calculando..."
                : generalFinance.data
                  ? moneyMdp(generalFinance.data.costoIndirecto)
                  : "—"}
            </strong>

            <small>
              {generalFinance.loading
                ? "Consultando resumen consolidado..."
                : generalFinance.error
                  ? generalFinance.error
                  : generalFinance.data
                    ? generalFinance.data.porcentajeCostoIndirecto.toFixed(2) +
                      "% del costo bruto total"
                    : "Consolidado no disponible"}
            </small>
          </article>
        </section>
        

        

        {/* =====================================================
            PCC_PIPELINE_COMPACT_FINAL_V1
            Pipeline compacto final.
            Solo lectura + seleccion local + editar existente.
        ===================================================== */}
        <section className="pdx-pipeline-final">
          {(() => {
            const activeStages = model.stageList.filter(
              (stage) =>
                Array.isArray(stage?.projects) &&
                stage.projects.length > 0
            );

            const selectedStage =
              model.stageList.find(
                (stage) =>
                  stage.key === pipelineStageKey &&
                  Array.isArray(stage?.projects) &&
                  stage.projects.length > 0
              ) ||
              activeStages[0] ||
              null;

            return (
              <>
                <header className="pdx-pipeline-final-head">
                  <div>
                    <span className="pdx-pipeline-final-eyebrow">
                      PIPELINE COMERCIAL
                    </span>

                    <h2>
                      Avance de oportunidades
                    </h2>
                  </div>

                  <div className="pdx-pipeline-final-total">
                    <strong>
                      {model.sales.length}
                    </strong>

                    <span>
                      {model.sales.length === 1
                        ? "oportunidad activa"
                        : "oportunidades activas"}
                    </span>
                  </div>
                </header>

                <div
                  className="pdx-pipeline-final-track"
                  role="group"
                  aria-label="Etapas comerciales"
                >
                  {model.stageList.map(
                    (stage, index) => {
                      const projects =
                        Array.isArray(stage?.projects)
                          ? stage.projects
                          : [];

                      const count = projects.length;

                      const stageAmount =
                        projects.reduce(
                          (total, project) =>
                            total +
                            projectAmount(project),
                          0
                        );

                      const isSelected =
                        selectedStage?.key ===
                        stage.key;

                      return (
                        <button
                          type="button"
                          key={
                            "pipeline-final-" +
                            stage.key
                          }
                          className={
                            "pdx-pipeline-final-stage" +
                            (count > 0
                              ? " has-projects"
                              : "") +
                            (isSelected
                              ? " is-selected"
                              : "")
                          }
                          disabled={count === 0}
                          onClick={() =>
                            setPipelineStageKey(
                              stage.key
                            )
                          }
                          aria-pressed={
                            isSelected
                          }
                        >
                          <span className="pdx-pipeline-final-number">
                            {String(
                              index + 1
                            ).padStart(2, "0")}
                          </span>

                          <span className="pdx-pipeline-final-stage-copy">
                            <strong>
                              {stage.label}
                            </strong>

                            <small>
                              {count === 1
                                ? "1 proyecto"
                                : count +
                                  " proyectos"}
                              {" · "}
                              {Number(
                                stage.probability ||
                                  0
                              )}
                              {"%"}
                            </small>
                          </span>

                          <span className="pdx-pipeline-final-amount">
                            {moneyMdp(
                              stageAmount
                            )}
                          </span>
                        </button>
                      );
                    }
                  )}
                </div>

                {selectedStage && (
                  <div className="pdx-pipeline-final-detail">
                    <div className="pdx-pipeline-final-detail-head">
                      <div>
                        <span>
                          ETAPA SELECCIONADA
                        </span>

                        <strong>
                          {selectedStage.label}
                        </strong>
                      </div>

                      <small>
                        {selectedStage.projects.length === 1
                          ? "1 oportunidad"
                          : selectedStage.projects.length +
                            " oportunidades"}
                      </small>
                    </div>

                    <div className="pdx-pipeline-final-projects">
                      {selectedStage.projects.map(
                        (project) => (
                          <button
                            type="button"
                            key={
                              project?.id ||
                              project?.proyectoId
                            }
                            className="pdx-pipeline-final-project"
                            onClick={() => {
                              if (
                                typeof onEditProject ===
                                "function"
                              ) {
                                onEditProject(
                                  project
                                );
                              }
                            }}
                          >
                            <span className="pdx-pipeline-final-project-main">
                              <strong>
                                {project?.nombre ||
                                  "Proyecto sin nombre"}
                              </strong>

                              <small>
                                {project?.cliente ||
                                  "Sin cliente"}
                              </small>
                            </span>

                            <span className="pdx-pipeline-final-project-next">
                              {project?.proximoPaso ||
                                "Sin siguiente accion"}
                            </span>

                            <span className="pdx-pipeline-final-project-money">
                              {moneyMdp(
                                projectAmount(
                                  project
                                )
                              )}
                            </span>

                            <span className="pdx-pipeline-final-project-action">
                              Editar
                            </span>
                          </button>
                        )
                      )}
                    </div>
                  </div>
                )}

                {!selectedStage && (
                  <div className="pdx-pipeline-final-empty">
                    Sin oportunidades comerciales activas.
                  </div>
                )}
              </>
            );
          })()}
        </section>

        <section className="pdx-board pdx-board-v3 pdx-board-six">
          <section className="pdx-column pdx-column-sales">
            <header>
              <strong>{TXT.sales}</strong>
              <span>
                {model.sales.length +
                  " · " +
                  moneyMdp(model.salesTotal)}
              </span>
            </header>

            <div className="pdx-column-body">
              {model.stageList.filter(
                (stage) => stage.projects.length > 0
              ).length === 0 ? (
                <div className="pdx-empty">
                  {TXT.noSales}
                </div>
              ) : (
                model.stageList
                  .filter(
                    (stage) =>
                      stage.projects.length > 0
                  )
                  .map((stage) => (
                    <section
                      className="pdx-stage"
                      key={stage.key}
                    >
                      <h4>
                        {displayOfficialStage(stage)}
                      </h4>

                      {stage.projects.map((project) => (
                        <Card
                          key={
                            project?.id ||
                            project?.proyectoId
                          }
                          project={project}
                          group="VENTA"
                          onEdit={onEditProject}
                          onFinance={onFinanceProject}
/>
                      ))}
                    </section>
                  ))
              )}
            </div>
          </section>

          <section className="pdx-column pdx-column-paused">
            <header>
              <strong>{TXT.paused}</strong>
              <span>
                {model.paused.length +
                  " · " +
                  moneyMdp(model.pausedTotal)}
              </span>
            </header>

            <div className="pdx-column-body">
              {model.paused.length === 0 ? (
                <div className="pdx-empty">
                  {TXT.noPaused}
                </div>
              ) : (
                model.paused.map((project) => (
                  <Card
                    key={
                      project?.id ||
                      project?.proyectoId
                    }
                    project={project}
                    group="VENTA"
                    onEdit={onEditProject}
                          onFinance={onFinanceProject}
/>
                ))
              )}
            </div>
          </section>

          <section className="pdx-column pdx-column-execution">
            <header>
              <strong>{TXT.execution}</strong>
              <span>
                {model.execution.length +
                  " · " +
                  moneyMdp(model.executionTotal)}
              </span>
            </header>

            <div className="pdx-column-body">
              {model.execution.length === 0 ? (
                <div className="pdx-empty">
                  {TXT.noExecution}
                </div>
              ) : (
                model.execution.map((project) => (
                  <Card
                    key={
                      project?.id ||
                      project?.proyectoId
                    }
                    project={project}
                    group="EJECUCION"
                    onEdit={onEditProject}
                          onFinance={onFinanceProject}
/>
                ))
              )}
            </div>
          </section>

          <section className="pdx-column pdx-column-closed">
            <header>
              <strong>{TXT.closed}</strong>
              <span>
                {model.closed.length +
                  " · " +
                  moneyMdp(model.closedTotal)}
              </span>
            </header>

            <div className="pdx-column-body">
              {model.closed.length === 0 ? (
                <div className="pdx-empty">
                  {TXT.noClosed}
                </div>
              ) : (
                model.closed.map((project) => (
                  <Card
                    key={
                      project?.id ||
                      project?.proyectoId
                    }
                    project={project}
                    group="CIERRE"
                    onEdit={onEditProject}
                          onFinance={onFinanceProject}
/>
                ))
              )}
            </div>
          </section>


          {/* IP_DEPOT_UX026B_SIX_SITUATIONS_V1 */}
          <section className="pdx-column pdx-column-discarded">
            <header>
              <strong>{TXT.discarded}</strong>
              <span>
                {model.notRealized.length +
                  " · " +
                  moneyMdp(
                    model.notRealized.reduce(
                      (total, project) =>
                        total + projectAmount(project),
                      0
                    )
                  )}
              </span>
            </header>

            <div className="pdx-column-body">
              {model.notRealized.length === 0 ? (
                <div className="pdx-empty">
                  {TXT.noDiscarded}
                </div>
              ) : (
                model.notRealized.map((project) => (
                  <Card
                    key={project?.id || project?.proyectoId}
                    project={project}
                    group="CIERRE"
                    onEdit={onEditProject}
                    onFinance={onFinanceProject}
                  />
                ))
              )}
            </div>
          </section>

          <section className="pdx-column pdx-column-dead">
            <header>
              <strong>{TXT.dead}</strong>
              <span>
                {model.dead.length +
                  " · " +
                  moneyMdp(
                    model.dead.reduce(
                      (total, project) =>
                        total + projectAmount(project),
                      0
                    )
                  )}
              </span>
            </header>

            <div className="pdx-column-body">
              {model.dead.length === 0 ? (
                <div className="pdx-empty">
                  {TXT.noDead}
                </div>
              ) : (
                model.dead.map((project) => (
                  <Card
                    key={project?.id || project?.proyectoId}
                    project={project}
                    group="CIERRE"
                    onEdit={onEditProject}
                    onFinance={onFinanceProject}
                  />
                ))
              )}
            </div>
          </section>

        </section>
      </main>
    </div>
  );
}